/**
 * Single Sign-On (SSO) helper for handing the logged-in user off to an
 * external module (Consent portal, DSPM, DSAR, DPIA...) without asking
 * them to log in again.
 *
 * Flow:
 *   1. Ask our backend for a short-lived SSO token for the target module
 *      -> POST /sso/generate-token (see Backend/app/api/v1/endpoints/sso.py)
 *   2. Backend returns `redirect_url` (built from the module's configured
 *      base URL) when that module has been wired up on the backend
 *      (CONSENT_MODULE_URL / DSPM_MODULE_URL / DSAR_MODULE_URL / DPIA_MODULE_URL
 *      in Backend/.env). If not configured, `redirect_url` is null.
 *   3. We redirect the browser there. The external module's OWN backend
 *      then calls POST /sso/validate-token on our backend to verify the
 *      token and log the user in on its side.
 */
import { apiFetch } from "../context/AppContext";

/** Modules that support an SSO hand-off from this dashboard. */
export const SSO_MODULES = ["consent", "dspm", "dsar", "dpia"];

async function readErrorDetail(res, fallback) {
  try {
    const data = await res.json();
    return data.detail || fallback;
  } catch {
    return fallback;
  }
}

/** Calls the backend to mint an SSO token for the given module. */
export async function generateSSOToken(module, targetUrl = null) {
  const res = await apiFetch("/sso/generate-token", {
    method: "POST",
    body: JSON.stringify({ module, target_url: targetUrl }),
  });
  if (!res.ok) {
    throw new Error(await readErrorDetail(res, "Could not start single sign-on"));
  }
  return res.json();
}

/**
 * Requests an SSO token and, if the module has an external URL configured
 * on the backend, redirects the browser there.
 *
 * @returns {Promise<boolean>} true if a redirect was performed, false if the
 *   module has no external URL configured (caller should fall back to its
 *   existing in-app behaviour, e.g. opening a local details modal).
 */
export async function redirectToModuleSSO(module, { targetUrl = null, newTab = false } = {}) {
  const data = await generateSSOToken(module, targetUrl);
  if (!data.redirect_url) return false;
  if (newTab) {
    const popup = window.open(data.redirect_url, "_blank", "noopener,noreferrer");
    if (!popup) throw new Error("Pop-up blocked — allow pop-ups to continue to the module");
  } else {
    window.location.href = data.redirect_url;
  }
  return true;
}
